import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { Play, ArrowRight, ArrowDown, ShieldCheck, LayoutDashboard, FileCheck2, Loader2 } from "lucide-react";
import { Button } from "../ui/button";
import { SectionHeading, Reveal } from "../common/Section";
import { track, EVENTS } from "../../lib/analytics";

const SCENARIOS = [
  { id: "passport", label: "Employee uploads a passport to an AI tool", risk: "Personal data · identity document", policy: "Restricted identity information", decision: "Block", action: "BLOCKED before submission", evidence: "Incident recorded · nothing retained", review: "Compliance notified", tone: "red" },
  { id: "advice", label: "AI chatbot attempts to give financial advice", risk: "Potentially unsuitable advice", policy: "No personalised financial advice", decision: "Escalate", action: "Response held · routed to a human", evidence: "Interaction logged with policy reference", review: "Adviser review required", tone: "amber" },
  { id: "injection", label: "Prompt injection / jailbreak attempt", risk: "Prompt injection", policy: "Instruction-override protection", decision: "Block", action: "Malicious instruction BLOCKED", evidence: "Attempt recorded for security", review: "Security team notified", tone: "red" },
  { id: "leak", label: "Sensitive customer information exposed", risk: "Personal data in the clear", policy: "Data minimisation & redaction", decision: "Redact", action: "Sensitive fields REDACTED", evidence: "Minimal reference retained", review: "No content stored", tone: "amber" },
  { id: "vulnerable", label: "Vulnerable customer conversation detected", risk: "Customer vulnerability", policy: "Vulnerable customer handling", decision: "Escalate", action: "Conversation flagged for care", evidence: "Case created with context", review: "Specialist reviewer assigned", tone: "amber" },
  { id: "agent", label: "AI agent attempts an unauthorised action", risk: "Out-of-policy tool action", policy: "Agent action allow-list", decision: "Block", action: "Action BLOCKED before execution", evidence: "Attempted action recorded", review: "Owner notified", tone: "red" },
];

const STAGES = ["Input", "Detection", "Policy", "Decision", "Action", "Evidence", "Human review"];
const toneText = { red: "text-signal-red", amber: "text-signal-amber", green: "text-signal-green" };
const toneBadge = { red: "border-signal-red/40 bg-signal-red/10 text-signal-red", amber: "border-signal-amber/40 bg-signal-amber/10 text-signal-amber", green: "border-signal-green/40 bg-signal-green/10 text-signal-green" };

export default function ScenarioRunner() {
  const [sel, setSel] = useState(SCENARIOS[0]);
  const [running, setRunning] = useState(false);
  const [step, setStep] = useState(-1);
  const [done, setDone] = useState(false);
  const timers = useRef([]);

  useEffect(() => () => timers.current.forEach(clearTimeout), []);

  const run = () => {
    timers.current.forEach(clearTimeout); timers.current = [];
    setRunning(true); setDone(false); setStep(0);
    track(EVENTS.SCENARIO_STARTED, { scenario: sel.id });
    STAGES.forEach((_, i) => {
      timers.current.push(setTimeout(() => {
        setStep(i);
        if (i === STAGES.length - 1) { setRunning(false); setDone(true); track(EVENTS.SCENARIO_COMPLETED, { scenario: sel.id }); }
      }, (i + 1) * 420));
    });
  };

  const pick = (s) => { setSel(s); setStep(-1); setDone(false); setRunning(false); timers.current.forEach(clearTimeout); };

  return (
    <section id="see-in-action" className="border-t border-border py-16 lg:py-24 bg-card">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <Reveal>
          <SectionHeading eyebrow="See it in action" title="See what norse3 would do." subtitle="Choose a realistic scenario and watch norse3 detect the risk, apply your policy, intervene and evidence it. Synthetic data only." />
        </Reveal>
        <div className="mt-10 grid lg:grid-cols-5 gap-6 items-start">
          <div className="lg:col-span-2 space-y-2">
            {SCENARIOS.map((s) => (
              <button key={s.id} onClick={() => pick(s)} data-testid={`scenario-${s.id}`}
                className={`w-full text-left rounded-xl border px-4 py-3 text-sm transition-all focus:outline-none focus:ring-2 focus:ring-primary/50 ${sel.id === s.id ? "border-primary/30 bg-primary/10 text-foreground shadow-sm" : "border-border bg-card text-muted-foreground hover:text-foreground hover:border-primary/25"}`}>
                {s.label}
              </button>
            ))}
          </div>

          <div className="lg:col-span-3 rounded-lg border border-border bg-muted/40 p-6 shadow-sm">
            <div className="flex items-center justify-between gap-3">
              <p className="text-sm text-foreground font-medium pr-4">{sel.label}</p>
              <Button size="sm" onClick={run} disabled={running} data-testid="scenario-run" className="shrink-0 bg-primary text-primary-foreground hover:bg-primary/90">
                {running ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Play className="mr-1.5 h-4 w-4" />} Run scenario
              </Button>
            </div>

            <div className="mt-5 space-y-1.5">
              {STAGES.map((stage, i) => {
                const shown = step >= i;
                const detail = done || shown ? [null, sel.risk, sel.policy, sel.decision, sel.action, sel.evidence, sel.review][i] : null;
                return (
                  <React.Fragment key={stage}>
                    <div className={`flex items-center justify-between rounded-lg border px-3.5 py-2.5 transition-all duration-300 ${shown ? "border-primary/25 bg-card opacity-100 shadow-sm" : "border-border bg-card/40 opacity-50"}`}>
                      <span className="text-xs uppercase tracking-wide text-muted-foreground">{stage}</span>
                      {detail && <span className={`text-sm font-medium ${i >= 3 ? toneText[sel.tone] : "text-foreground"}`}>{detail}</span>}
                    </div>
                    {i < STAGES.length - 1 && <div className="flex justify-center text-primary/50"><ArrowDown className="h-3 w-3" /></div>}
                  </React.Fragment>
                );
              })}
            </div>

            {done && (
              <div className="mt-5 rounded-xl border border-border bg-card p-4" data-testid="scenario-result">
                <span className={`inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-semibold uppercase tracking-wide ${toneBadge[sel.tone]}`}><ShieldCheck className="h-3.5 w-3.5" /> {sel.decision} · evidence recorded</span>
                <div className="mt-4 flex flex-wrap gap-2.5">
                  <Button asChild size="sm" variant="outline" className="border-border bg-transparent hover:bg-muted text-foreground/80">
                    <Link to="/interactive-demo#heimdall" onClick={() => track(EVENTS.HEIMDALL_OPENED, { scenario: sel.id })}><LayoutDashboard className="mr-1.5 h-4 w-4" /> Open incident in Heimdall</Link>
                  </Button>
                  <Button asChild size="sm" variant="outline" className="border-border bg-transparent hover:bg-muted text-foreground/80">
                    <Link to="/interactive-demo#evidence"><FileCheck2 className="mr-1.5 h-4 w-4" /> Generate sample evidence record</Link>
                  </Button>
                </div>
                <div className="mt-4 rounded-lg border border-primary/25 bg-primary/10 p-3.5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <p className="text-sm text-foreground">Want to see this against your own AI environment?</p>
                  <Button asChild size="sm" className="shrink-0 bg-primary text-primary-foreground hover:bg-primary/90">
                    <Link to="/book-a-demo">Book a 20-minute walkthrough <ArrowRight className="ml-1.5 h-4 w-4" /></Link>
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
